import { resolveAndApplySpeakerForCompositionElement } from '@src/utility/compositionSettings';
import { recordPlacementEdgeOverrides } from '@src/utility/placementOverrides';
import { makeAutoObservable, runInAction } from 'mobx';

import { type ElementState, mapToElementState } from '../renderer/ElementState';
import { KARAOKE_TRACK_NUMBER } from './VideoCreatorStore';

import type VideoCreatorStore from './VideoCreatorStore';
import type { RootStore } from '@src/stores-v2';

export interface ElementPlacementOverride {
  globalTime?: number;
  proposedGlobalTime?: number;
  duration?: number;
  trimStart?: number;
  yOffset?: number;
  track?: number;
}

export default class TimelineStore {
  private videoCreator: VideoCreatorStore;
  private rootStore: RootStore;

  elementsWithPlacementOverridesHaveDifferentOffsets: boolean = false;
  // swapElementPlacements
  overrideTimelineElementsState: Record<
    string,
    Partial<ElementState> & ElementPlacementOverride
  > = {};
  framerate: number = 24; //default legacy framerate

  // Frame-sized tolerance for float comparisons during timeline edits
  get frameEpsilon(): number {
    return 1 / (this.framerate || 24);
  }

  isDifferent(a: number, b: number): boolean {
    return Math.abs(a - b) > this.frameEpsilon;
  }

  private _shouldCalcTimelineScale = false;

  /**
   * Tracks whether the last drag operation snapped to a boundary.
   * Used to determine if frame snapping should be applied when finalizing placements.
   */
  lastDragOperationSnapped: boolean = false;

  /**
   * Active element IDs that are actual timeline elements (not inner compositions/representations).
   * Used to highlight timeline elements when selection originates from canvas/renderer.
   */
  activeTimelineElementIds: string[] = [];

  trimmingElementIds: Record<string, boolean> = {};

  /**
   * Per-element buffered trim values during an active drag.
   * Not applied to the element (or the renderer) until the drag completes —
   * this keeps the Bar and video preview visually frozen while the user
   * scrubs the trim handles, with only the pink ghost overlay reacting.
   */
  trimPreview: Record<
    string,
    { globalTime: number; trimStart: number; duration: number }
  > = {};

  constructor(videoCreator: VideoCreatorStore, rootStore: RootStore) {
    this.videoCreator = videoCreator;
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetElementsStateOverrides() {
    this.overrideTimelineElementsState = {};
    this.elementsWithPlacementOverridesHaveDifferentOffsets = false;
    this.lastDragOperationSnapped = false;
  }

  /**
   * Set the active timeline element IDs.
   * These are the actual timeline element IDs (parent elements that span the current time).
   */
  setActiveTimelineElements(...elementIds: string[]): void {
    this.activeTimelineElementIds = elementIds;
  }

  /**
   * Clear the active timeline element IDs.
   */
  clearActiveTimelineElements(): void {
    this.activeTimelineElementIds = [];
  }

  /**
   * Select a whole track, replacing any element selection.
   *
   * Selection spans three fields — the creator store's `activeElementIds` and
   * `selectedTrack` plus this store's `activeTimelineElementIds` mirror — so
   * the transition lives here rather than at each call site. Clearing only the
   * first two left Delete hitting the stale element, since
   * DeleteActiveElementService reads the mirror first.
   */
  async selectTrack(track: number): Promise<void> {
    this.clearActiveTimelineElements();
    // Assigns selectedTrack = -1, so the track has to be set after it.
    await this.videoCreator.setActiveElements();
    runInAction(() => {
      this.videoCreator.selectedTrack = track;
    });
  }

  /**
   * Check if an element ID is in the active timeline elements.
   */
  isTimelineElementActive(elementId: string): boolean {
    return this.activeTimelineElementIds.includes(elementId);
  }

  beginTrim(elementId: string): void {
    this.trimmingElementIds = {
      ...this.trimmingElementIds,
      [elementId]: true,
    };
  }

  endTrim(elementId: string): void {
    if (!this.trimmingElementIds[elementId]) return;
    const next = { ...this.trimmingElementIds };
    delete next[elementId];
    this.trimmingElementIds = next;

    const previewNext = { ...this.trimPreview };
    delete previewNext[elementId];
    this.trimPreview = previewNext;
  }

  isTrimming(elementId: string): boolean {
    return !!this.trimmingElementIds[elementId];
  }

  setTrimPreview(
    elementId: string,
    values: { globalTime: number; trimStart: number; duration: number },
  ): void {
    this.trimPreview = { ...this.trimPreview, [elementId]: values };
  }

  getTrimPreview(elementId: string) {
    return this.trimPreview[elementId];
  }

  /**
   * Clear a single element's buffered trim preview without touching others.
   * Used when a trim drag switches direction from contract → extend.
   */
  clearTrimPreview(elementId: string): void {
    if (!this.trimPreview[elementId]) return;
    const next = { ...this.trimPreview };
    delete next[elementId];
    this.trimPreview = next;
  }

  /**
   * Clear a single element's placement override without touching others.
   * Used when a trim drag switches direction from extend → contract so the
   * stale extend override doesn't get committed as if it were the final
   * drag state.
   */
  clearPlacementOverride(elementId: string): void {
    if (!this.overrideTimelineElementsState[elementId]) return;
    runInAction(() => {
      const next = { ...this.overrideTimelineElementsState };
      delete next[elementId];
      this.overrideTimelineElementsState = next;
    });
  }

  /**
   * Fire the editor trim command synchronously for a buffered preview so
   * the element's new duration/position and the rippled neighbors land in
   * the same MobX transaction — the Bar and neighbors then repaint in a
   * single React pass.
   *
   * Bypasses applyPlacements on purpose: that path awaits
   * getNaturalMediaDimensions before dispatching the command, which splits
   * the visual update across microtasks (Bar grows first, neighbors lag a
   * tick behind). Uses preloadedNaturalDimensions instead.
   *
   * Fade / stacked-layout / playhead bookkeeping that applyPlacements
   * normally runs is deferred to a microtask so it doesn't block the visual
   * commit.
   */
  commitTrimPreview(elementId: string): void {
    const preview = this.trimPreview[elementId];
    if (!preview) return;

    const editor = this.rootStore.editorStore.editor;
    if (!editor) return;

    const element = this.rootStore.editorStore.editor.getElementById(elementId);
    if (!element) return;
    const elementState = mapToElementState(element.toJSON());

    let globalTime = preview.globalTime;
    let trimStart = preview.trimStart;
    let duration = preview.duration;

    const isExtendCommit = duration > elementState.duration;
    if (isExtendCommit) {
      // Re-apply the media / trimStart / min-duration clamps that were
      // deferred during the drag so the extension can't exceed the source.
      const isOriginalVideoOrAudio =
        this.videoCreator.isOriginalVideoElement(elementState.source) ||
        elementState.source.type === 'audio';
      if (
        isOriginalVideoOrAudio &&
        elementState.mediaDuration &&
        elementState.mediaDuration + elementState.trimStart <
          duration + trimStart
      ) {
        duration = elementState.mediaDuration;
      }
      if (
        ['audio', 'video'].includes(elementState.source.type) &&
        trimStart < 0
      ) {
        duration += trimStart;
        globalTime -= trimStart;
        trimStart = 0;
      }
      if (duration < 0.5) duration = 0.5;
    }

    const naturalMediaDimensions = this.videoCreator.preloadedNaturalDimensions;
    const sceneDetections = this.rootStore.editorStore.sceneDetectionsForEdit(
      this.videoCreator.sceneDetections,
    );
    const faceDetections = this.videoCreator.faceDetections ?? undefined;
    const framingService = this.rootStore.editorStore.framingService;

    const trimStartChanged = this.isDifferent(
      elementState.source.trim_start || 0,
      trimStart,
    );
    const durationChanged = this.isDifferent(elementState.duration, duration);

    if (trimStartChanged) {
      // Left-handle trim/extend: shifts globalTime + trimStart + duration.
      editor.commandTrimElementStart(
        elementId,
        globalTime,
        true,
        sceneDetections,
        naturalMediaDimensions,
        faceDetections,
        framingService,
      );
    } else if (durationChanged) {
      // Right-handle trim/extend: only duration changes.
      editor.commandTrimElementEnd(
        elementId,
        globalTime + duration,
        true,
        sceneDetections,
        naturalMediaDimensions,
        faceDetections,
        framingService,
      );
    }

    // Defer fade-crossfade / stacked-layout / playhead reconciliation to a
    // microtask so the visual commit (Bar + neighbors) paints together in
    // the current frame. These are bookkeeping side effects that don't
    // affect the element's geometry.
    queueMicrotask(() => {
      this.reconcileAfterTrimCommit(elementId, {
        globalTime,
        trimStart,
        duration,
      });
    });
  }

  /**
   * Post-trim reconciliation: playhead preservation when the timeline is
   * extended. Runs async after the visual commit so geometry repaints first.
   */
  private reconcileAfterTrimCommit(
    elementId: string,
    placement: { globalTime: number; trimStart: number; duration: number },
  ): void {
    // Preserve playhead when extending past current timeline duration.
    const newElementEnd = placement.globalTime + placement.duration;
    if (newElementEnd > this.videoCreator.duration) {
      const currentPlayheadTime = this.videoCreator.time;
      setTimeout(() => this.videoCreator.setTime(currentPlayheadTime, true));
    }
  }

  applyPlacementOffsetToActiveElements(offset: number, track: number) {
    const { activeElementIds } = this.videoCreator;
    if (!activeElementIds.length) {
      return;
    }
    const allElements = this.rootStore.editorStore.allElementStates;
    this.applyPlacementOffsetToElements(
      allElements.filter(element =>
        activeElementIds.includes(element.source.id),
      ),
      offset,
      track,
    );
  }

  getActiveTimelineElementIds(): string[] {
    return this.activeTimelineElementIds;
  }

  private static ifElementsOverlapsWith(
    elements: ElementState[],
    newPlacement: ElementState,
    otherElements: ElementState[],
  ) {
    return otherElements.some(elOther => {
      if (elements.find(el => el.source.id === elOther.source.id)) {
        return false;
      }
      const elOtherEnd = elOther.globalTime + elOther.duration;
      const newPlacementEnd = newPlacement.globalTime + newPlacement.duration;
      // allow overlap at the edges
      return (
        Math.max(elOther.globalTime, newPlacement.globalTime) <
        Math.min(elOtherEnd, newPlacementEnd)
      );
    });
  }

  /**
   * Checks if applying the current placement overrides would create any overlaps.
   * Used as a safeguard before committing placements - if overlaps would result,
   * the operation is reverted (noop).
   *
   * @param movingElements - Elements being moved (have placement overrides)
   * @param allElements - All elements in the timeline
   * @returns true if any overlap would be created, false if placements are valid
   */
  private wouldPlacementsCreateOverlaps(
    movingElements: ElementState[],
    allElements: ElementState[],
  ): boolean {
    const movingElementIds = new Set(movingElements.map(el => el.source.id));

    for (const element of movingElements) {
      const override = this.overrideTimelineElementsState[element.source.id];
      const targetTrack =
        (override as Partial<ElementState>).track ?? element.track;
      const proposedGlobalTime = override.globalTime ?? element.globalTime;
      const proposedDuration = override.duration ?? element.duration;

      // Build proposed placement for overlap check
      const proposedPlacement: ElementState = {
        ...element,
        globalTime: proposedGlobalTime,
        duration: proposedDuration,
        track: targetTrack,
      };

      // Get other elements on target track (excluding all moving elements)
      const trackElements = allElements.filter(
        el => el.track === targetTrack && !movingElementIds.has(el.source.id),
      );

      if (
        TimelineStore.ifElementsOverlapsWith(
          [element],
          proposedPlacement,
          trackElements,
        )
      ) {
        return true;
      }
    }
    return false;
  }

  applyPlacementOverrideToElement(
    elementId: string,
    placement: ElementPlacementOverride,
  ) {
    runInAction(() => {
      this.overrideTimelineElementsState[elementId] = placement;
    });
  }

  async applyPlacements(enableMovingBetweenTracks: boolean = false) {
    // Get elements from EditorStore (single source of truth) instead of videoCreator.state
    const allElements = Array.from(
      this.rootStore.editorStore.tracksMap.values(),
    )
      .flat()
      .map(element => mapToElementState(element.toJSON()));
    const overridenElementIds = Object.keys(this.overrideTimelineElementsState);
    const selectedElements = allElements.filter(el =>
      overridenElementIds.includes(el.source.id),
    );
    if (overridenElementIds.length === 0 || selectedElements.length === 0)
      return;

    // Validate that proposed placements don't create overlaps.
    // If any overlap would result, revert by resetting overrides (noop).
    if (
      this.wouldPlacementsCreateOverlaps(selectedElements, allElements) &&
      enableMovingBetweenTracks
    ) {
      this.resetElementsStateOverrides();
      return;
    }

    // PLAT-10921 — record the timeline edges this move changed, so a pinned
    // graphic's edge can be enforced / accepted / rebound by the reconcile that
    // follows. Done only after the overlap check passes so a reverted move
    // doesn't record an override that never happened.
    this.recordDraggedPlacementOverrides(selectedElements);

    const currentPlayheadTime = this.videoCreator.time;

    let mainTrackElementsSelected = [];

    if (overridenElementIds.length > 1) {
      mainTrackElementsSelected = selectedElements.filter(el =>
        this.videoCreator.isOriginalVideoElement(el.source),
      );
    } else {
      mainTrackElementsSelected = [
        selectedElements.find(
          el =>
            !this.videoCreator.isImageElement(el) &&
            !(
              el.source.type === 'video' &&
              !this.videoCreator.isOriginalVideoElement(el.source)
            ),
        ),
      ].filter(Boolean) as ElementState[];
    }

    if (this.elementsWithPlacementOverridesHaveDifferentOffsets) {
      const sortedElements = selectedElements.sort(
        (a, b) => a.globalTime - b.globalTime,
      );
      const sortedPlacements = sortedElements.map(el => {
        return {
          element: el,
          placement: {
            globalTime:
              this.overrideTimelineElementsState[el.source.id].globalTime || 0,
          },
        };
      });
      this.changeElementPlacements(sortedPlacements);
    } else {
      const firstElement = mainTrackElementsSelected[0] ?? selectedElements[0];
      const newPlacement = {
        ...firstElement,
        ...this.overrideTimelineElementsState[firstElement.source.id],
      };

      // only for moving elements
      // TODO handle move and trim cases separately
      if (
        mainTrackElementsSelected.length > 0 &&
        firstElement.duration === newPlacement.duration
      ) {
        const lastElement =
          mainTrackElementsSelected[mainTrackElementsSelected.length - 1];
        if (mainTrackElementsSelected.length > 1) {
          newPlacement.duration =
            lastElement.globalTime +
            lastElement.duration -
            firstElement.globalTime;
        }
        const overlapsWith = TimelineStore.ifElementsOverlapsWith(
          mainTrackElementsSelected,
          newPlacement,
          allElements.filter(
            el => el.track === (newPlacement.track || firstElement.track),
          ),
        );

        if (overlapsWith || mainTrackElementsSelected.length > 1) {
          const nextElement = allElements.find(
            el =>
              // ignoring newPlacement.track here because track changes are not
              // allowed for main video elements or multi-element moves
              el.track === lastElement.track &&
              el.globalTime > lastElement.globalTime,
          );
          // @ts-ignore
          const prevElement = allElements.findLast(
            (el: ElementState) =>
              // ignoring newPlacement.track here because track changes are not
              // allowed for main video elements or multi-element moves
              el.track === firstElement.track &&
              el.globalTime < firstElement.globalTime,
          );

          if (
            (prevElement?.globalTime || 0) + (prevElement?.duration || 0) >
            newPlacement.globalTime
          ) {
            newPlacement.globalTime = this.videoCreator.snapTime(
              (prevElement?.globalTime || 0) + (prevElement?.duration || 0),
            );
          } else if (
            nextElement &&
            nextElement.globalTime <
              newPlacement.globalTime + newPlacement.duration
          ) {
            newPlacement.globalTime = this.videoCreator.snapTime(
              Math.max(nextElement.globalTime - newPlacement.duration, 0),
            );
          }
        }

        this.applyPlacementOffsetToActiveElements(
          newPlacement.globalTime - firstElement.globalTime,
          newPlacement.track,
        );
      }

      const leftmostElementTime = selectedElements.reduce((acc, el) => {
        const elTime =
          this.overrideTimelineElementsState[el.source.id].globalTime ??
          el.globalTime;
        return elTime < acc ? elTime : acc;
      }, Infinity);
      if (leftmostElementTime < 0) {
        newPlacement.globalTime -= leftmostElementTime;
        this.applyPlacementOffsetToElements(
          selectedElements,
          newPlacement.globalTime - firstElement.globalTime,
          newPlacement.track,
        );
      }

      if (overridenElementIds.length > 1) {
        await this.shiftElements(
          overridenElementIds,
          newPlacement.globalTime - firstElement.globalTime,
        );
      } else {
        await this.updateElementPlacement(firstElement, newPlacement);
      }
    }

    // Restore original playhead position if extending timeline.
    //
    // `overrideTimelineElementsState[el.source.id]` can be undefined here
    // when an upstream branch (e.g. `changeElementPlacements` /
    // `updateElementPlacement` above) cleared the entry for this element
    // before we got here — `selectedElements` was snapshotted from the
    // override keys at the top of the function but the map can mutate
    // mid-flow. Skip those: if there's no override, the element didn't
    // extend the timeline by definition.
    const isExtendingTimeline = selectedElements.some(el => {
      const override = this.overrideTimelineElementsState[el.source.id];
      if (!override) return false;
      const newDuration = override.duration || el.duration;
      const newGlobalTime = override.globalTime ?? el.globalTime;
      const newElementEndTime = newGlobalTime + newDuration;
      return (
        newDuration > el.duration &&
        newElementEndTime > this.videoCreator.duration
      );
    });
    if (isExtendingTimeline) {
      setTimeout(() => this.videoCreator.setTime(currentPlayheadTime, true));
    }

    // Rest on timeout since weird render chain breaking stuff
    // happens when we apply placement and then immediately reset the state
    // Update: the issue is that Timeline.tsx uses videoCreatorStore.tracks which
    // get updated later, after the source refresh timeout
    this.videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(() => {
      this.resetElementsStateOverrides();
    });
  }

  /**
   * PLAT-10921 — record which TIMELINE edges the creator just moved on every
   * dragged/trimmed composition, then let each edge be evaluated (Enforce /
   * Accept / Rebind). Replaces the old `manuallyPositioned` write, which
   * unpinned a dragged end card outright; a drag no longer unpins anything — it
   * records an override and gets evaluated.
   *
   * The flags are pushed straight into the editor's extraElementData copy (which
   * `PlacementReconciler` reads) so the *same* move command's reconcile sees
   * them — the store→editor MobX reaction can lag when the mutation happens
   * inside an action.
   */
  private recordDraggedPlacementOverrides(elements: ElementState[]) {
    const video = this.videoCreator.currentVideo;
    if (!video) return;

    const root = video.extraElementData;
    if (!root || typeof root !== 'object' || Array.isArray(root)) return;

    const editor = this.rootStore.editorStore.editor;
    if (!editor) return;

    // Built at most once per drag, and only if a `selection` graphic is actually
    // among the moved elements — `getFullTranscript` is O(all words) and every
    // drag would otherwise pay for it.
    let transcript: ReturnType<typeof editor.getFullTranscript> | undefined;
    const getTranscript = () => (transcript ??= editor.getFullTranscript());

    let changed = false;
    for (const el of elements) {
      const override = this.overrideTimelineElementsState[el.source.id];
      if (!override) continue;

      const didChange = recordPlacementEdgeOverrides({
        video,
        elementId: el.source.id,
        previous: { time: el.globalTime, duration: el.duration },
        next: {
          time: override.globalTime ?? el.globalTime,
          duration: override.duration ?? el.duration,
        },
        transcript: getTranscript,
      });
      if (didChange) changed = true;
    }

    if (changed) {
      editor.passExtraElementData(video.extraElementData);
    }
  }

  // offset - number of seconds to shift the elements by
  applyPlacementOffsetToElements(
    elements: ElementState[],
    offset: number,
    track: number,
  ) {
    runInAction(() => {
      elements.forEach(element => {
        this.overrideTimelineElementsState[element.source.id] = {
          globalTime: element.globalTime + offset,
          track: track,
        };
      });
    });
  }

  async changeElementPlacements(
    placements: { element: ElementState; placement: { globalTime: number } }[],
  ) {
    const { editor } = this.rootStore.editorStore;
    if (editor) {
      editor.commandChangeElementPlacements(
        placements.map(p => {
          return {
            elementId: p.element.source.id,
            newStartTime: p.placement.globalTime,
          };
        }),
      );
    }
  }

  applyTimelinePlacementOverrideToElement(
    element: ElementState,
    placement: ElementPlacementOverride,
  ) {
    runInAction(() => {
      this.overrideTimelineElementsState[element.source.id] = placement;
    });
  }

  setElementsWithPlacementOverridesHaveDifferentOffsets(value: boolean) {
    runInAction(() => {
      this.elementsWithPlacementOverridesHaveDifferentOffsets = value;
    });
  }

  setLastDragOperationSnapped(value: boolean) {
    runInAction(() => {
      this.lastDragOperationSnapped = value;
    });
  }

  get shouldCalcTimelineScale() {
    return this._shouldCalcTimelineScale;
  }

  setShouldCalcTimelineScale(value: boolean) {
    this._shouldCalcTimelineScale = value;
  }

  async shiftElements(elementIds: string[], timeShift: number) {
    const { editor } = this.rootStore.editorStore;
    editor.commandShiftMultipleElements(
      elementIds,
      timeShift,
      !this.lastDragOperationSnapped,
    );
  }

  async updateElementPlacement(
    element: ElementState,
    placement: Pick<
      ElementState,
      'duration' | 'globalTime' | 'trimStart' | 'track'
    >,
  ) {
    if (
      !this.isDifferent(element.globalTime, placement.globalTime) &&
      !this.isDifferent(element.duration, placement.duration)
    )
      return;

    if (
      this.videoCreator.isOriginalVideoElement(element.source) &&
      this.rootStore.transcriptionStore.getFinalTranscriptionElements()?.length
    ) {
      await this.updateMainVideoElementPlacementAndTranscript(
        element,
        placement,
      );
      return;
    }

    if (element.track === KARAOKE_TRACK_NUMBER) {
      this.updateKaraokeElementPlacement(element, placement);
      return;
    }

    // Use headless editor commands for all elements (audio, image, text, etc.)
    this.updateMediaElementPlacement(element, placement);
    if (element.source.type === 'composition') {
      await resolveAndApplySpeakerForCompositionElement({
        elementId: element.source.id,
        editorStore: this.videoCreator.rootStore.editorStore as any,
        datoClientStore: this.rootStore.datoClientStore,
        videoCreator: this.videoCreator,
        timelineTime: placement.globalTime,
      });
    }
  }

  private updateKaraokeElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ) {
    // TODO: handle subtitles also
    if (!this.rootStore.editorStore.editor) return;
    const karaokeLine = this.rootStore.karaokeStore.getKaraokeLineById(
      element.source.id,
    );
    if (!karaokeLine) {
      console.error('Karaoke line not found for element', element.source.id);
      return;
    }
    const startOffset = placement.globalTime - karaokeLine.transcriptStartTime;
    const endOffset =
      placement.globalTime + placement.duration - karaokeLine.transcriptEndTime;

    this.rootStore.editorStore.editor.commandAdjustKaraokeClipTime(
      element.source.id,
      { startSeconds: startOffset, endSeconds: endOffset },
    );
  }

  private async updateMediaElementPlacement(
    element: ElementState,
    placement: Pick<
      ElementState,
      'duration' | 'globalTime' | 'trimStart' | 'track'
    >,
  ) {
    if (!this.rootStore.editorStore.editor) return;

    // all of these time comparisons need to factor in an epsilon due to
    // floating point precision issues. Differences smaller than 1/framerate
    // should be ignored.
    if (
      this.isDifferent(
        element.source.trim_start || 0,
        placement.trimStart || 0,
      ) ||
      (!placement.trimStart &&
        element.duration < placement.duration &&
        this.isDifferent(element.globalTime, placement.globalTime))
    ) {
      // Trim start
      const naturalMediaDimensions =
        await this.videoCreator.getNaturalMediaDimensions();
      this.rootStore.editorStore.editor.commandTrimElementStart(
        element.source.id,
        placement.globalTime,
        !this.lastDragOperationSnapped,
        this.rootStore.editorStore.sceneDetectionsForEdit(
          this.videoCreator.sceneDetections,
        ),
        naturalMediaDimensions,
        this.videoCreator.faceDetections ?? undefined,
        this.rootStore.editorStore.framingService,
      );
    } else if (
      this.isDifferent(element.globalTime, placement.globalTime) ||
      element.track !== placement.track
    ) {
      // Move element (time and/or track change)
      // Clear yOffset before track change to prevent visual overshoot.
      // When an element changes tracks, the yOffset (used during dragging to show
      // the element moving toward a new track) should be cleared since the element
      // is now positioned by its new track container.
      if (
        placement.track !== undefined &&
        element.track !== placement.track &&
        this.overrideTimelineElementsState[element.source.id]?.yOffset
      ) {
        runInAction(() => {
          const override =
            this.overrideTimelineElementsState[element.source.id];
          if (override) {
            delete override.yOffset;
          }
        });
      }
      this.rootStore.editorStore.editor.commandShiftElement(
        element.source.id,
        placement.globalTime,
        placement.track,
        !this.lastDragOperationSnapped,
      );
    } else if (this.isDifferent(element.duration, placement.duration)) {
      // Trim end
      const naturalMediaDimensions =
        await this.videoCreator.getNaturalMediaDimensions();
      this.rootStore.editorStore.editor.commandTrimElementEnd(
        element.source.id,
        placement.duration + placement.globalTime,
        !this.lastDragOperationSnapped,
        this.rootStore.editorStore.sceneDetectionsForEdit(
          this.videoCreator.sceneDetections,
        ),
        naturalMediaDimensions,
        this.videoCreator.faceDetections ?? undefined,
        this.rootStore.editorStore.framingService,
      );
    }
  }

  private async updateMainVideoElementPlacementAndTranscript(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ) {
    // For main video element, we only allow moving and extending duration
    if (
      this.isDifferent(element.source.trim_start || 0, placement.trimStart || 0)
    ) {
      const naturalMediaDimensions =
        await this.videoCreator.getNaturalMediaDimensions();
      this.rootStore.editorStore.editor.commandTrimElementStart(
        element.source.id,
        placement.globalTime,
        true,
        this.rootStore.editorStore.sceneDetectionsForEdit(
          this.videoCreator.sceneDetections,
        ),
        naturalMediaDimensions,
        this.videoCreator.faceDetections ?? undefined,
        this.rootStore.editorStore.framingService,
        //!this.lastDragOperationSnapped,
      );
    } else if (this.isDifferent(element.globalTime, placement.globalTime)) {
      this.rootStore.editorStore.editor.commandShiftElement(
        element.source.id,
        placement.globalTime,
        element.track, // original video elements cannot change tracks, keep current track
        true,
        //!this.lastDragOperationSnapped,
      );
    } else if (this.isDifferent(element.duration, placement.duration)) {
      const naturalMediaDimensions =
        await this.videoCreator.getNaturalMediaDimensions();
      this.rootStore.editorStore.editor.commandTrimElementEnd(
        element.source.id,
        placement.duration + placement.globalTime,
        true,
        this.rootStore.editorStore.sceneDetectionsForEdit(
          this.videoCreator.sceneDetections,
        ),
        naturalMediaDimensions,
        this.videoCreator.faceDetections ?? undefined,
        this.rootStore.editorStore.framingService,
        //!this.lastDragOperationSnapped,
      );
    }

    return;
  }
}
